/**
 * mSTEM Tutorial Modal — presentational 2-column Master-Detail layout
 * (PRJ-06 / PRJ-07 / LLD UC 09-10).
 *
 * Wave 3a (Plan 03-07a): infrastructure SHELL only — component, sidebar
 * with chapter list, content panel with section rendering OR graceful
 * empty-state fallback (`gen.tutorial.contentError`). Footer nav with
 * prev/next + page indicator. ARIA roles (navigation / link / article /
 * aria-current) per LLD UC 09 STT 1-4.
 *
 * Wave 3b (Plan 03-07b): populates ide-chapters.js with full section
 * content + 32 SVG illustrations. Component code path is unchanged —
 * once sections[] is non-empty, the same JSX renders heading + body +
 * figure for every section.
 *
 * Container lives in src/containers/mstem-tutorial-modal.jsx — it owns
 * keyboard nav (ArrowDown / ArrowUp / Escape) + scrollIntoView on
 * chapter change. This component is pure: every interaction goes through
 * the props (onSetActive / onNext / onPrev / onClose).
 */
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import {defineMessages, FormattedMessage, injectIntl, intlShape} from 'react-intl';

import Modal from '../../containers/modal.jsx';

import styles from './mstem-tutorial-modal.css';

const messages = defineMessages({
    ideTitle: {
        defaultMessage: 'Hướng dẫn sử dụng IDE',
        description: 'IDE tutorial modal title (PRJ-06 / LLD UC 09)',
        id: 'gen.tutorial.ide.title'
    },
    firmwareTitle: {
        defaultMessage: 'Hướng dẫn cập nhật Firmware',
        description: 'Firmware tutorial modal title (PRJ-07 / LLD UC 10)',
        id: 'gen.tutorial.firmware.title'
    },
    prevChapter: {
        defaultMessage: '← Chương trước',
        description: 'Tutorial footer previous-chapter button (PRJ-06)',
        id: 'gen.tutorial.nav.prev'
    },
    nextChapter: {
        defaultMessage: 'Chương sau →',
        description: 'Tutorial footer next-chapter button (PRJ-06)',
        id: 'gen.tutorial.nav.next'
    },
    chapterListLabel: {
        defaultMessage: 'Danh sách chương',
        description: 'Tutorial sidebar aria-label (PRJ-06)',
        id: 'gen.tutorial.nav.chapterList'
    },
    contentError: {
        defaultMessage: 'Không thể hiển thị hướng dẫn',
        description: 'Tutorial content load error per LLD UC 09/10 (PRJ-06/07)',
        id: 'gen.tutorial.contentError'
    }
});

const MstemTutorialModalComponent = ({
    intl,
    type,
    chapters,
    activeChapterId,
    onSetActive,
    onPrev,
    onNext,
    onClose
}) => {
    const modalTitleMsg = type === 'firmware' ? messages.firmwareTitle : messages.ideTitle;
    const activeIndex = chapters.findIndex(c => c.id === activeChapterId);
    const safeIndex = activeIndex >= 0 ? activeIndex : 0;
    const activeChapter = chapters[safeIndex] || null;
    const total = chapters.length;
    const hasSections = activeChapter &&
        Array.isArray(activeChapter.sections) &&
        activeChapter.sections.length > 0;

    return (
        <Modal
            className={styles.modalContent}
            contentLabel={intl.formatMessage(modalTitleMsg)}
            id="tutorialModal"
            onRequestClose={onClose}
        >
            <div className={styles.tutorialBody}>
                <nav
                    className={styles.tutorialSidebar}
                    role="navigation"
                    aria-label={intl.formatMessage(messages.chapterListLabel)}
                >
                    {chapters.map((ch, idx) => {
                        const isActive = ch.id === activeChapterId;
                        return (
                            <div
                                key={ch.id}
                                data-chapter-id={ch.id}
                                role="link"
                                tabIndex={0}
                                className={classNames(styles.chapterItem, {
                                    [styles.chapterItemActive]: isActive
                                })}
                                onClick={() => onSetActive(ch.id)} // eslint-disable-line react/jsx-no-bind
                                onKeyDown={e => { // eslint-disable-line react/jsx-no-bind
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        onSetActive(ch.id);
                                    }
                                }}
                                aria-current={isActive ? 'page' : null}
                            >
                                <span className={styles.chapterIndex}>{`${idx + 1}.`}</span>
                                <span className={styles.chapterTitle}>{ch.title}</span>
                            </div>
                        );
                    })}
                </nav>
                <article
                    className={styles.tutorialContent}
                    role="article"
                    aria-labelledby={activeChapter ? `chapter-title-${activeChapter.id}` : null}
                >
                    {activeChapter && hasSections ? (
                        <React.Fragment>
                            <h2
                                id={`chapter-title-${activeChapter.id}`}
                                className={styles.chapterH2}
                            >
                                {activeChapter.title}
                            </h2>
                            {activeChapter.sections.map((section, idx) => (
                                <section
                                    key={idx}
                                    className={styles.section}
                                >
                                    <h3 className={styles.sectionH3}>{section.heading}</h3>
                                    <p className={styles.sectionBody}>{section.body}</p>
                                    {section.illustration && (
                                        <figure className={styles.figure}>
                                            <img
                                                src={section.illustration}
                                                alt={section.illustrationAlt || section.heading}
                                                className={styles.illustration}
                                            />
                                        </figure>
                                    )}
                                </section>
                            ))}
                        </React.Fragment>
                    ) : (
                        <div className={styles.errorPanel}>
                            <FormattedMessage {...messages.contentError} />
                        </div>
                    )}
                </article>
            </div>
            <div className={styles.tutorialFooter}>
                <button
                    type="button"
                    className={styles.btnNav}
                    disabled={safeIndex === 0}
                    aria-disabled={safeIndex === 0}
                    onClick={onPrev}
                >
                    <FormattedMessage {...messages.prevChapter} />
                </button>
                <span className={styles.pageIndicator}>{`${safeIndex + 1} / ${total}`}</span>
                <button
                    type="button"
                    className={styles.btnNav}
                    disabled={safeIndex >= total - 1}
                    aria-disabled={safeIndex >= total - 1}
                    onClick={onNext}
                >
                    <FormattedMessage {...messages.nextChapter} />
                </button>
            </div>
        </Modal>
    );
};

MstemTutorialModalComponent.propTypes = {
    activeChapterId: PropTypes.string,
    chapters: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        sections: PropTypes.array // eslint-disable-line react/forbid-prop-types
    })).isRequired,
    intl: intlShape.isRequired,
    onClose: PropTypes.func.isRequired,
    onNext: PropTypes.func.isRequired,
    onPrev: PropTypes.func.isRequired,
    onSetActive: PropTypes.func.isRequired,
    type: PropTypes.oneOf(['ide', 'firmware']).isRequired
};

MstemTutorialModalComponent.defaultProps = {
    activeChapterId: null
};

export default injectIntl(MstemTutorialModalComponent);
